import { useMutation } from '@apollo/client';
import { PersonaMessages } from '@bechtel/client-common';
import { DELETE_MILESTONE_ATTACHMENT } from '@bechtel/client-gql';
import { Avatar, Box, Button, ButtonAppearance, ButtonSize, Icon, Intrinsic, Text } from '@bechtel/gds';
import {
  Attachment,
  DeleteMilestoneAttachmentError,
  DeleteMilestoneAttachmentInput,
  DeleteMilestoneAttachmentPayload,
} from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { findIndex, get, groupBy, map, orderBy } from 'lodash';
import { FC, Fragment, useState } from 'react';
import { useAppUser } from '~/components/molecules';
import { ImageViewer, useImageViewerState } from '~/components/organisms/image-viewer';
import { useDate } from '~/hooks/use-date';
import { useName } from '~/hooks/use-name';
import { AttachmentGroupStyled, BlockImgStyled, DeleteAttachmentStyled } from './styled';
import { AttachmentImage, ViewMilestoneAttachmentProps } from './types';

export interface UploadedFile extends File {
  preview: string;
  attachmentId: string;
}

export const ViewMilestoneAttachment: FC<ViewMilestoneAttachmentProps> = ({
  setAttachments,
  attachments,
  commodityMilestoneId,
  canDeleteMilestoneAttachment,
}) => {
  const { user: loggedInUser } = useAppUser();
  const loggedUserId = loggedInUser?.crewMember?.id;
  const { getFullName } = useName();
  const { i18n } = useLingui();
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const { open: openImageViewer, close: closeImageViewer, visible: visibleImageViewer } = useImageViewerState(false);
  const { formatISODate } = useDate();
  const sortedAttachments = orderBy(attachments, ['createdAt'], ['desc']);
  const groupedByUsersAttachments = Object.values(groupBy(sortedAttachments, (attachment) => attachment.creator.id));
  const attachmentImages: AttachmentImage[] = [];

  const [addRemoveMilestoneAttachmentError, setAddRemoveMilestoneAttachmentError] = useState<
    DeleteMilestoneAttachmentError | ApolloError | null
  >(null);
  const [deleteMilestoneAttachment] = useMutation<
    { deleteMilestoneAttachment: DeleteMilestoneAttachmentPayload },
    { input: DeleteMilestoneAttachmentInput }
  >(DELETE_MILESTONE_ATTACHMENT, {
    onCompleted: (data: { deleteMilestoneAttachment: DeleteMilestoneAttachmentPayload }) => {
      const deleteMilestoneAttachmentError = get(data, 'deleteMilestoneAttachment.error', null);
      if (!deleteMilestoneAttachmentError) return;
      setAddRemoveMilestoneAttachmentError(deleteMilestoneAttachmentError);
    },
    onError: (error) => {
      setAddRemoveMilestoneAttachmentError(error);
    },
  });

  return (
    <Fragment>
      {visibleImageViewer && (
        <ImageViewer
          images={imageUrls}
          selectedImageIndex={selectedImageIndex}
          visible={visibleImageViewer}
          closable={true}
          onDismiss={closeImageViewer}
        />
      )}

      {groupedByUsersAttachments.length > 0 ? (
        map(groupedByUsersAttachments, (attachmentsForUser, index) => {
          return (
            <Fragment key={attachmentsForUser[0].creator.id}>
              {map(Object.values(groupBy(attachmentsForUser, 'createdAt')), (attachmentGroupByUpdateDate) => {
                return (
                  <AttachmentGroupStyled
                    key={`${attachmentsForUser[0].creator.id}-${attachmentGroupByUpdateDate[0].createdAt}`}
                  >
                    <Box
                      alignItems={{ sm: 'center' }}
                      justifyContent={{ sm: 'space-between' }}
                      px={{ sm: '500' }}
                      py={{ sm: '600' }}
                    >
                      <Box alignItems={{ sm: 'center' }}>
                        <Avatar
                          src={attachmentsForUser[0].creator.imageUrl}
                          data-testid={`attachment-avatar-${attachmentsForUser[0].creator.id}-${attachmentGroupByUpdateDate[0].createdAt}`}
                        />
                        <Box flexDirection={{ sm: 'column' }} ml={{ sm: '400' }}>
                          <Box py={{ sm: '200' }}>
                            <Text
                              weight="600"
                              color="grey.1000"
                              size="300"
                              data-testid={`avatar-name-${attachmentsForUser[0].creator.id}-${attachmentGroupByUpdateDate[0].createdAt}`}
                            >
                              {getFullName(
                                attachmentGroupByUpdateDate[0].creator.firstName,
                                attachmentGroupByUpdateDate[0].creator.lastName
                              )}
                            </Text>
                          </Box>
                          <Box pb={{ sm: '200' }}>
                            <Text
                              size="250"
                              color="grey.600"
                              data-testid={`avatar-persona-${attachmentsForUser[0].creator.id}-${attachmentGroupByUpdateDate[0].createdAt}`}
                            >
                              {attachmentGroupByUpdateDate[0].creator.persona
                                ? i18n._(PersonaMessages[attachmentGroupByUpdateDate[0].creator.persona])
                                : ''}
                            </Text>
                          </Box>
                        </Box>
                      </Box>
                      <Box flexDirection={{ sm: 'column' }}>
                        <Box pb={{ sm: '200' }}>
                          <Text
                            family="graphik"
                            size="200"
                            color="grey.1000"
                            data-testid={`avatar-photo-attached-${attachmentsForUser[0].creator.id}-${attachmentGroupByUpdateDate[0].createdAt}`}
                          >
                            {t`Photos Attached:`} {attachmentGroupByUpdateDate.length}
                          </Text>
                        </Box>
                        <Box pb={{ sm: '200' }}>
                          <Text
                            family="graphik"
                            size="200"
                            color="grey.1000"
                            data-testid={`updated-at-${attachmentsForUser[0].creator.id}-${attachmentGroupByUpdateDate[0].createdAt}`}
                          >
                            {t`Uploaded:`} {formatISODate(attachmentGroupByUpdateDate[0].createdAt)}
                          </Text>
                        </Box>
                      </Box>
                    </Box>

                    {map(attachmentGroupByUpdateDate, (attachment: Attachment) => {
                      attachmentImages.push({ url: attachment.url, id: attachment.id });
                      return (
                        <Box
                          style={{ position: 'relative' }}
                          key={attachment.id}
                          px={{ sm: '500' }}
                          pb={{ sm: '500' }}
                          onClick={() => {
                            setSelectedImageIndex(findIndex(attachmentImages, (img) => img.id == attachment.id));
                            setImageUrls(map(attachmentImages, (attachment) => attachment.url));
                            openImageViewer();
                          }}
                        >
                          <Intrinsic ratio={{ sm: '16:9' }}>
                            <BlockImgStyled src={attachment.url} data-testid={`attachment-url-${attachment.id}`} />
                          </Intrinsic>
                          {/*  Can Delete Permission and if the uploaded one is the deleted one also */}

                          {canDeleteMilestoneAttachment && loggedUserId == attachment.creator.id && (
                            <DeleteAttachmentStyled key={attachment.id}>
                              <Button
                                key={attachment.id}
                                appearance={ButtonAppearance.SECONDARY}
                                size={ButtonSize.SMALL}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  const { data: deleteMilestoneAttachmentData } = await deleteMilestoneAttachment({
                                    variables: {
                                      input: {
                                        attachmentId: attachment.id,
                                        commodityMilestoneId: commodityMilestoneId,
                                      },
                                    },
                                  });
                                  if (
                                    deleteMilestoneAttachmentData &&
                                    !deleteMilestoneAttachmentData.deleteMilestoneAttachment.error
                                  ) {
                                    setAttachments(
                                      deleteMilestoneAttachmentData?.deleteMilestoneAttachment?.milestone?.attachments
                                    );
                                  }
                                }}
                              >
                                <Box>
                                  <Box mr={{ sm: '300' }}>
                                    <Icon icon="trash-alt" fillColor="red.800" />
                                  </Box>
                                  <Trans>Delete</Trans>
                                </Box>
                              </Button>
                            </DeleteAttachmentStyled>
                          )}
                        </Box>
                      );
                    })}
                  </AttachmentGroupStyled>
                );
              })}
            </Fragment>
          );
        })
      ) : (
        <Box p={{ sm: '500' }}>
          <Trans>No attachments found.</Trans>
        </Box>
      )}
    </Fragment>
  );
};
